<div class="channels-container">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <h2>Channels</h2>
      <p class="section-description">Manage communication channels for message processing</p>
    </div>
    <button class="btn btn-primary" (click)="startCreate()" [disabled]="isCreating || isSaving">
      <i class="fas fa-plus"></i> Add Channel
    </button>
  </div>

  <!-- Messages -->
  <div class="message error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>
  <div class="message success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <i class="fas fa-spinner fa-spin"></i> Loading channels...
  </div>

  <!-- Filters -->
  <div class="filters-row" *ngIf="!isLoading">
    <div class="search-box">
      <i class="fas fa-search"></i>
      <input type="text" [(ngModel)]="searchQuery" placeholder="Search channels..." />
    </div>
    <div class="filter-group" *ngIf="!organizationId">
      <label>Organization:</label>
      <select [(ngModel)]="orgFilter">
        <option value="all">All</option>
        <option *ngFor="let org of organizations" [value]="org.ID">{{ org.Name }}</option>
      </select>
    </div>
    <div class="filter-group">
      <label>Status:</label>
      <select [(ngModel)]="statusFilter">
        <option value="all">All</option>
        <option value="Active">Active</option>
        <option value="Pending">Pending</option>
        <option value="Revoked">Revoked</option>
      </select>
    </div>
  </div>

  <!-- Create Form -->
  <div class="create-form card" *ngIf="isCreating">
    <h3>New Channel</h3>
    <div class="form-grid">
      <div class="form-group">
        <label>Name *</label>
        <input type="text" [(ngModel)]="createForm.name" placeholder="Channel name" />
      </div>
      <div class="form-group" *ngIf="!organizationId">
        <label>Organization *</label>
        <select [(ngModel)]="createForm.organizationId">
          <option *ngFor="let org of organizations" [value]="org.ID">{{ org.Name }}</option>
        </select>
      </div>
      <div class="form-group">
        <label>Channel Type *</label>
        <select [(ngModel)]="createForm.channelTypeId">
          <option *ngFor="let ct of channelTypes" [value]="ct.ID">{{ ct.Name }}</option>
        </select>
      </div>
      <div class="form-group">
        <label>External Identifier</label>
        <input type="text" [(ngModel)]="createForm.externalIdentifier" placeholder="e.g., email address, channel ID" />
        <small class="help-text">Provider-specific identifier</small>
      </div>
      <div class="form-group full-width">
        <label>Description</label>
        <textarea [(ngModel)]="createForm.description" placeholder="Describe this channel" rows="2"></textarea>
      </div>
      <div class="form-group">
        <label>Check Frequency (minutes)</label>
        <input type="number" [(ngModel)]="createForm.checkFrequency" min="1" />
      </div>
      <div class="form-group">
        <label>Action Inherit Mode</label>
        <select [(ngModel)]="createForm.actionInheritMode">
          <option value="Organization">Organization Only</option>
          <option value="ChannelType">Channel Type</option>
          <option value="Channel">Channel Only</option>
          <option value="Combined">Combined (Type + Channel)</option>
        </select>
      </div>
      <div class="form-group">
        <label>Credential</label>
        <select [(ngModel)]="createForm.credentialId">
          <option [ngValue]="null">-- No credential --</option>
          <option *ngFor="let cred of getCredentialsForOrganization(createForm.organizationId)" [ngValue]="cred.ID">{{ displayCredentialName(cred.Name) }}</option>
        </select>
        <small class="help-text">Select credentials for this channel's communication provider</small>
      </div>
      <div class="form-group">
        <label>Status</label>
        <select [(ngModel)]="createForm.status">
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Revoked">Revoked</option>
        </select>
      </div>
    </div>
    <div class="form-actions">
      <button class="btn btn-secondary" (click)="cancelCreate()" [disabled]="isSaving">Cancel</button>
      <button class="btn btn-primary" (click)="saveCreate()" [disabled]="isSaving">
        <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
        {{ isSaving ? 'Saving...' : 'Create Channel' }}
      </button>
    </div>
  </div>

  <!-- Channels List -->
  <div class="channels-list" *ngIf="!isLoading">
    <div class="channel-card card" *ngFor="let cd of filteredChannels" [class.editing]="cd.isEditing">
      <!-- View Mode -->
      <ng-container *ngIf="!cd.isEditing">
        <div class="channel-header">
          <div class="channel-icon">
            <i class="fas" [ngClass]="getChannelTypeIcon(cd.channelType?.Name || '')"></i>
          </div>
          <div class="channel-info">
            <h4>{{ cd.channel.Name }}</h4>
            <div class="channel-meta">
              <span class="org-badge" *ngIf="cd.organization && !organizationId">
                <i class="fas fa-building"></i> {{ cd.organization.Name }}
              </span>
              <span class="type-badge" *ngIf="cd.channelType">
                {{ cd.channelType.Name }}
              </span>
              <span class="status-badge" [ngClass]="getStatusClass(cd.channel.Status)">
                {{ cd.channel.Status }}
              </span>
            </div>
          </div>
        </div>
        <p class="channel-description" *ngIf="cd.channel.Description">{{ cd.channel.Description }}</p>
        <div class="channel-details">
          <div class="detail-item" *ngIf="cd.channel.ExternalIdentifier">
            <span class="detail-label">External ID:</span>
            <span class="detail-value">{{ cd.channel.ExternalIdentifier }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Check Frequency:</span>
            <span class="detail-value">Every {{ cd.channel.CheckFrequency }} min</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Action Mode:</span>
            <span class="detail-value">{{ getInheritModeLabel(cd.extension?.ActionInheritMode) }}</span>
          </div>
          <div class="detail-item" *ngIf="cd.extension?.CredentialID">
            <span class="detail-label">Credential:</span>
            <span class="detail-value credential-value">
              <i class="fas fa-key"></i> {{ getCredentialName(cd.extension?.CredentialID) }}
            </span>
          </div>
        </div>
        <div class="channel-actions">
          <button class="btn btn-secondary" (click)="startEdit(cd)" [disabled]="isSaving">
            <i class="fas fa-edit"></i> Edit
          </button>
          <button class="btn btn-icon btn-danger" title="Delete" (click)="confirmDelete(cd)" [disabled]="isSaving">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </ng-container>

      <!-- Edit Mode -->
      <ng-container *ngIf="cd.isEditing">
        <div class="edit-form">
          <div class="form-row">
            <div class="form-group">
              <label>Name</label>
              <input type="text" [(ngModel)]="cd.editForm.name" />
            </div>
            <div class="form-group" *ngIf="!organizationId">
              <label>Organization</label>
              <select [(ngModel)]="cd.editForm.organizationId">
                <option *ngFor="let org of organizations" [value]="org.ID">{{ org.Name }}</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Channel Type</label>
              <select [(ngModel)]="cd.editForm.channelTypeId">
                <option *ngFor="let ct of channelTypes" [value]="ct.ID">{{ ct.Name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>External Identifier</label>
              <input type="text" [(ngModel)]="cd.editForm.externalIdentifier" />
            </div>
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea [(ngModel)]="cd.editForm.description" rows="2"></textarea>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Check Frequency (min)</label>
              <input type="number" [(ngModel)]="cd.editForm.checkFrequency" min="1" />
            </div>
            <div class="form-group">
              <label>Action Inherit Mode</label>
              <select [(ngModel)]="cd.editForm.actionInheritMode">
                <option value="Organization">Organization Only</option>
                <option value="ChannelType">Channel Type</option>
                <option value="Channel">Channel Only</option>
                <option value="Combined">Combined</option>
              </select>
            </div>
            <div class="form-group">
              <label>Status</label>
              <select [(ngModel)]="cd.editForm.status">
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
                <option value="Revoked">Revoked</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Credential</label>
              <select [(ngModel)]="cd.editForm.credentialId">
                <option [ngValue]="null">-- No credential --</option>
                <option *ngFor="let cred of getCredentialsForOrganization(cd.editForm.organizationId)" [ngValue]="cred.ID">{{ displayCredentialName(cred.Name) }}</option>
              </select>
            </div>
          </div>
          <div class="form-actions">
            <button class="btn btn-secondary" (click)="cancelEdit(cd)" [disabled]="isSaving">Cancel</button>
            <button class="btn btn-primary" (click)="saveEdit(cd)" [disabled]="isSaving">
              <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
              Save
            </button>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Empty State -->
    <div class="empty-state card" *ngIf="filteredChannels.length === 0 && !isCreating">
      <i class="fas fa-broadcast-tower"></i>
      <p>No channels found</p>
      <button class="btn btn-primary" (click)="startCreate()">Create Your First Channel</button>
    </div>
  </div>

  <!-- Confirm Dialog -->
  <div class="modal-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDialogYes()" [disabled]="isSaving">
          <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
          Delete
        </button>
      </div>
    </div>
  </div>
</div>
